import { isClerkAPIResponseError } from "@clerk/nextjs/errors";
import { useCallback } from "react";

import { useToast } from "@/hooks/useToast";
import { logger } from "@/util/logger";

/**
 * Custom hook to handle Clerk errors consistently across the application.
 * Provides a centralized way to display error messages from Clerk operations.
 */
export const useClerkErrorHandler = () => {
  const { errorToast } = useToast();

  const handleClerkError = useCallback(
    (error: unknown, context?: string) => {
      logger.error(`Clerk error${context ? ` in ${context}` : ""}:`, error);

      if (isClerkAPIResponseError(error)) {
        const firstError = error.errors?.[0];
        const errorCode = firstError?.code;
        const errorMessage = firstError?.message || error.message;

        // Handle specific error codes with user-friendly messages
        switch (errorCode) {
          case "form_identifier_not_found":
            errorToast({
              text: "Account with this email address does not exist.",
            });
            break;

          case "too_many_requests":
            errorToast({
              text: "Too many requests. Please try again in 10 minutes.",
            });
            break;

          case "revoked_invitation":
            errorToast({
              text: "This user is not allowed to sign up, please contact admin or support.",
            });
            break;

          case "session_token_and_uat_claim_check_failed":
            errorToast({
              text: "Your session has expired. Please sign in again.",
            });
            break;

          case "authentication_invalid":
            errorToast({
              text: "Authentication failed. Please check your credentials and try again.",
            });
            break;

          case "user_locked":
            errorToast({
              text: "Your account has been locked. Please contact support.",
            });
            break;

          case "verification_expired":
            errorToast({
              text: "The verification link has expired. Please request a new one.",
            });
            break;

          case "verification_failed":
            errorToast({
              text: "Verification failed. Please try again or request a new verification link.",
            });
            break;

          case "email_address_not_found":
            errorToast({
              text: "Email address not found. Please check and try again.",
            });
            break;

          case "incorrect_password":
            errorToast({
              text: "Incorrect password. Please try again.",
            });
            break;

          case "user_exists":
            errorToast({
              text: "An account with this email already exists. Please sign in instead.",
            });
            break;

          case "password_pwned":
            errorToast({
              text: "This password has been compromised. Please choose a different password.",
            });
            break;

          case "password_too_common":
            errorToast({
              text: "This password is too common. Please choose a more secure password.",
            });
            break;

          case "form_password_length_too_short":
            errorToast({
              text: "Password is too short. Please choose a longer password.",
            });
            break;

          case "form_password_no_uppercase_letter":
            errorToast({
              text: "Password must contain at least one uppercase letter.",
            });
            break;

          case "form_password_no_lowercase_letter":
            errorToast({
              text: "Password must contain at least one lowercase letter.",
            });
            break;

          case "form_password_no_number":
            errorToast({
              text: "Password must contain at least one number.",
            });
            break;

          case "form_password_no_special_char":
            errorToast({
              text: "Password must contain at least one special character.",
            });
            break;

          case "organization_not_found":
            errorToast({
              text: "Organization not found. Please contact your administrator.",
            });
            break;

          case "organization_invitation_not_found":
            errorToast({
              text: "Invitation not found or has expired. Please contact your administrator.",
            });
            break;

          case "saml_connection_not_found":
            errorToast({
              text: "SSO connection not found. Please contact your administrator.",
            });
            break;

          case "oauth_access_denied":
            errorToast({
              text: "Access denied. Please grant the necessary permissions and try again.",
            });
            break;

          case "captcha_invalid":
            errorToast({
              text: "Captcha verification failed. Please try again.",
            });
            break;

          case "maintenance_mode":
            errorToast({
              text: "The service is temporarily unavailable. Please try again later.",
            });
            break;

          case "session_exists":
            // Don't show toast for session_exists as it will be handled by retry mechanism
            logger.warn("Session already exists, will be handled by retry mechanism");
            break;

          default:
            // For unknown error codes, display the error message from Clerk
            errorToast({
              text: errorMessage || "An authentication error occurred. Please try again.",
            });
            break;
        }
      } else {
        // Handle non-Clerk errors
        const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
        errorToast({
          text: `Authentication failed: ${errorMessage}`,
        });
      }
    },
    [errorToast],
  );

  return {
    handleClerkError,
  };
};
